import { Bot, MapPin, User } from "lucide-react";
import { FEATURE_KEYS, type OrderListItem } from "@school-sales-ai/shared";
import { useHasClientFeature } from "@/hooks/useClientFeatures";
import { formatMxn } from "@/lib/format-mxn";
import { formatLinesPreview } from "@/lib/order-board";
import { timeAgo } from "@/lib/time-ago";

/**
 * The moment that matters for the column the order sits in: when it was closed,
 * when it was confirmed, or — for a cart — when it was started.
 */
function relevantAt(order: OrderListItem): string {
  return order.closedAt ?? order.confirmedAt ?? order.createdAt;
}

/**
 * What a board card says about an order. Shared by the draggable desktop card,
 * its drag overlay and the mobile card, so the three cannot drift.
 */
export function OrderCardBody({ order }: { order: OrderListItem }): React.JSX.Element {
  const preview = formatLinesPreview(order);
  // A pickup-only shop (orders without «Envío a domicilio») has no address to
  // miss, so it must never see the «Sin dirección» warning.
  const delivers = useHasClientFeature(FEATURE_KEYS.delivery);
  return (
    <>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-[13px] font-semibold text-ink-900">
            {order.leadName ?? <span className="italic text-ink-400">Sin nombre</span>}
          </p>
          <p className="font-mono text-[10.5px] text-ink-500">{order.sessionRef}</p>
        </div>
        <span className="shrink-0 text-[13px] font-semibold text-ink-900">
          {formatMxn(order.totalCents, order.currency)}
        </span>
      </div>

      {preview && <p className="line-clamp-2 text-[12px] text-ink-600">{preview}</p>}

      {/* Only while it still has to go somewhere. A missing address is what
          stops the person driving, so it is the one warning on the card. */}
      {delivers &&
        order.status === "confirmed" &&
        (order.deliveryAddress ? (
          <p
            className="flex items-center gap-1 truncate text-[11.5px] text-ink-600"
            title={order.deliveryAddress}
          >
            <MapPin className="h-3 w-3 shrink-0 text-ink-400" aria-hidden />
            <span className="truncate">{order.deliveryAddress}</span>
          </p>
        ) : (
          <p className="flex items-center gap-1 text-[11.5px] font-medium text-amber-700">
            <MapPin className="h-3 w-3 shrink-0" aria-hidden />
            Sin dirección
          </p>
        ))}

      <div className="flex items-center justify-between gap-2 text-[10.5px] text-ink-400">
        {order.confirmedAt ? (
          <span className="flex items-center gap-1 rounded-full bg-ink-050 px-1.5 py-0.5 text-ink-600">
            {order.confirmedByAgent ? (
              <Bot className="h-3 w-3" aria-hidden />
            ) : (
              <User className="h-3 w-3" aria-hidden />
            )}
            {order.confirmedByAgent ? "Agente" : "Equipo"}
          </span>
        ) : (
          <span />
        )}
        <span>{timeAgo(relevantAt(order))}</span>
      </div>
    </>
  );
}
